import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { getServerURL } from "../../../utils/funcs/get-serverURL";
import LoadingSpinner from "../../loading-spinner";

interface SMSLog {
  _id: string;
  legacyCertId: string | null;
  customerName: string;
  customerNumber: string;
  expiryDate: string;
  messageSent: string;
  status: "success" | "failed";
  messageId?: string;
  error?: string;
  sentAt: string;
}

interface UpcomingSMS {
  _id: string;
  customerName: string;
  customerNumber: string | null;
  address: string | null;
  expiryDate: string;
}

interface Pagination {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export default function SMSLogs({ user }: any) {
  const { getToken } = useAuth();
  const [logs, setLogs] = useState<SMSLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<SMSLog | null>(null);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [bookingsData, setBookingsData] = useState<any[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState<string | null>(null);

  // Upcoming SMS state
  const [upcomingRecords, setUpcomingRecords] = useState<UpcomingSMS[]>([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [upcomingError, setUpcomingError] = useState<string | null>(null);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [upcomingPaginating, setUpcomingPaginating] = useState(false);
  const [upcomingPagination, setUpcomingPagination] = useState<Pagination>({
    currentPage: 1, pageSize: 10, totalCount: 0, totalPages: 1,
  });

  const serverURL = getServerURL();
  const getAuthHeader = useCallback(async () => {
    const token = await getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const fetchUpcomingSMS = useCallback(async (page: number, isPaginating = false) => {
    try {
      if (isPaginating) {
        setUpcomingPaginating(true);
      } else {
        setUpcomingLoading(true);
        setUpcomingError(null);
      }
      const authHeader = await getAuthHeader();
      const response = await axios.post(
        `${serverURL}/sms/upcoming`,
        { page, limit: 10 },
        { headers: authHeader }
      );
      if (response.data.success) {
        setUpcomingRecords(response.data.data);
        setUpcomingPagination(response.data.pagination);
        setUpcomingPage(page);
      } else {
        setUpcomingError("Failed to load upcoming SMS reminders");
      }
    } catch (err) {
      setUpcomingError("Error fetching upcoming SMS reminders");
    } finally {
      setUpcomingLoading(false);
      setUpcomingPaginating(false);
    }
  }, [getAuthHeader, serverURL]);

  const fetchSMSLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const authHeader = await getAuthHeader();
      const response = await axios.get(`${serverURL}/sms/logs`, {
        headers: authHeader,
      });
      
      if (response.data.success) {
        setLogs(response.data.logs);
      } else {
        setError("Failed to load SMS logs");
      }
    } catch (err) {
      console.error("Error fetching SMS logs:", err);
      setError("Error fetching SMS logs from server");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, serverURL]);

  const fetchAnalytics = useCallback(async (messageId: string) => {
    try {
      setAnalyticsLoading(true);
      setAnalyticsError(null);
      setAnalyticsData(null);
      const authHeader = await getAuthHeader();
     
      
      const response = await axios.get(`${serverURL}/sms/${messageId}/analytics`, {
        headers: authHeader,
      });
      
      
      if (response.data.success) {
        setAnalyticsData(response.data.analytics);
      } else {
        setAnalyticsError(response.data.error || "Failed to load analytics");
      }
    } catch (err) {
      console.error("📊 [fetchAnalytics] Error fetching analytics:", err);
      if (axios.isAxiosError(err)) {
        console.error(`   Status: ${err.response?.status}`);
        console.error(`   Response data:`, err.response?.data);
        setAnalyticsError(`Error: ${err.response?.status} - ${err.response?.data?.error || err.message}`);
      } else {
        setAnalyticsError("Error fetching analytics from server");
      }
    } finally {
      setAnalyticsLoading(false);
    }
  }, [getAuthHeader, serverURL]);

  const fetchBookings = useCallback(async (log: SMSLog) => {
    try {
      setBookingsLoading(true);
      setBookingsError(null);
      setBookingsData([]);
      const authHeader = await getAuthHeader();
      
      const response = await axios.post(`${serverURL}/bookings-after-inspection`, {
        customerName: log.customerName,
        customerNumber: log.customerNumber,
        smsSentDate: log.sentAt
      }, {
        headers: authHeader,
      });
      
      if (response.data.success) {
        setBookingsData(response.data.bookings || []);
      } else {
        setBookingsError(response.data.error || "Failed to load bookings");
      }
    } catch (err) {
      console.error("📅 [fetchBookings] Error fetching bookings:", err);
      if (axios.isAxiosError(err)) {
        console.error(`   Status: ${err.response?.status}`);
        console.error(`   Response data:`, err.response?.data);
        setBookingsError(`Error: ${err.response?.status} - ${err.response?.data?.error || err.message}`);
      } else {
        setBookingsError("Error fetching bookings from server");
      }
    } finally {
      setBookingsLoading(false);
    }
  }, [getAuthHeader, serverURL]);

  useEffect(() => {
    fetchSMSLogs();
    fetchUpcomingSMS(1);
  }, [fetchSMSLogs, fetchUpcomingSMS]);

  useEffect(() => {
    if (selectedLog && selectedLog.status === "success") {
      fetchBookings(selectedLog);
    }
  }, [selectedLog, fetchBookings]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB");
  };

  const formatExpiryDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB");
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "red" }}>
        <p>{error}</p>
        <button 
          onClick={fetchSMSLogs}
          style={{
            padding: "10px 20px",
            backgroundColor: "#00adb5",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            marginTop: "10px"
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>

      {/* ── Upcoming SMS Reminders ── */}
      <div style={{ marginBottom: "40px" }}>
        <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0 }}>Upcoming SMS Reminders</h2>
          <div style={{ color: "#666", fontSize: "0.9em" }}>
            {!upcomingLoading && `${upcomingPagination.totalCount} certificates not yet contacted`}
          </div>
        </div>

        {upcomingLoading ? (
          <div className="loadingBtn"><div /><div /><div /><div /></div>
        ) : upcomingError ? (
          <p style={{ color: "#dc2626" }}>{upcomingError}</p>
        ) : upcomingRecords.length === 0 ? (
          <p style={{ color: "#16a34a", fontStyle: "italic" }}>✓ No certificates awaiting SMS reminders.</p>
        ) : (
          <>
            {/* Pagination controls */}
            {upcomingPagination.totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "12px", gap: "4px" }}>
                <button
                  onClick={() => fetchUpcomingSMS(Math.max(1, upcomingPage - 1), true)}
                  disabled={upcomingPage === 1 || upcomingPaginating}
                  style={paginationBtnStyle(upcomingPage === 1 || upcomingPaginating, false)}
                >
                  ← Prev
                </button>
                {(() => {
                  const pages: (number | "…")[] = [];
                  for (let i = 1; i <= upcomingPagination.totalPages; i++) {
                    if (i === 1 || i === upcomingPagination.totalPages || Math.abs(i - upcomingPage) <= 1) {
                      pages.push(i);
                    } else if (pages[pages.length - 1] !== "…") {
                      pages.push("…");
                    }
                  }
                  return pages.map((p, idx) =>
                    p === "…" ? (
                      <span key={`e-${idx}`} style={{ padding: "4px 4px", fontSize: "0.75em", color: "#999" }}>…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => fetchUpcomingSMS(p, true)}
                        disabled={upcomingPaginating}
                        style={paginationBtnStyle(upcomingPaginating, upcomingPage === p)}
                      >
                        {p}
                      </button>
                    )
                  );
                })()}
                <button
                  onClick={() => fetchUpcomingSMS(Math.min(upcomingPagination.totalPages, upcomingPage + 1), true)}
                  disabled={upcomingPage === upcomingPagination.totalPages || upcomingPaginating}
                  style={paginationBtnStyle(upcomingPage === upcomingPagination.totalPages || upcomingPaginating, false)}
                >
                  Next →
                </button>
              </div>
            )}

            <div style={{ overflowX: "auto", borderRadius: "6px", border: "1px solid #e0e0e0" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff", minWidth: "500px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e0e0e0" }}>
                    <th style={thStyle}>Customer</th>
                    <th style={thStyle}>Phone</th>
                    <th style={thStyle}>Address</th>
                    <th style={thStyle}>Expiry Date</th>
                    <th style={thStyle}>Days Left</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingRecords.map((record, index) => {
                    const expiry = new Date(record.expiryDate);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const daysLeft = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    const urgentColor = daysLeft <= 7 ? "#dc2626" : daysLeft <= 14 ? "#d97706" : "#16a34a";
                    return (
                      <tr
                        key={record._id}
                        style={{
                          borderBottom: "1px solid #e0e0e0",
                          backgroundColor: index % 2 === 0 ? "#fff" : "#f8f9fa",
                          opacity: upcomingPaginating ? 0.5 : 1,
                        }}
                      >
                        <td style={tdStyle}>{record.customerName}</td>
                        <td style={tdStyle}>{record.customerNumber || <span style={{ color: "#bbb" }}>—</span>}</td>
                        <td style={tdStyle}>{record.address ? (record.address.length > 35 ? record.address.slice(0, 35) + "…" : record.address) : <span style={{ color: "#bbb" }}>—</span>}</td>
                        <td style={tdStyle}>{expiry.toLocaleDateString("en-GB")}</td>
                        <td style={tdStyle}>
                          <span style={{
                            backgroundColor: daysLeft <= 7 ? "#fee2e2" : daysLeft <= 14 ? "#fef3c7" : "#dcfce7",
                            color: urgentColor,
                            padding: "3px 8px",
                            borderRadius: "4px",
                            fontWeight: 600,
                            fontSize: "0.8em",
                          }}>
                            {daysLeft} day{daysLeft !== 1 ? "s" : ""}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── Sent SMS Logs ── */}
      <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0 }}>SMS Logs</h2>
        <div style={{ color: "#666", fontSize: "0.95em" }}>
          Total: <strong>{logs.length}</strong> messages
        </div>
      </div>

      {logs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", backgroundColor: "#f9f9f9", borderRadius: "4px" }}>
          <p>No SMS logs found</p>
        </div>
      ) : (
        <>
          {/* Statistics */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "30px" }}>
            <div style={{ 
              padding: "15px", 
              backgroundColor: "#e8f5e9", 
              borderRadius: "4px", 
              borderLeft: "4px solid #4caf50",
              textAlign: "center"
            }}>
              <div style={{ fontSize: "1.5em", fontWeight: "600", color: "#2e7d32" }}>
                {logs.filter(l => l.status === "success").length}
              </div>
              <div style={{ color: "#555", marginTop: "5px", fontSize: "0.9em" }}>Delivered</div>
            </div>
            <div style={{ 
              padding: "15px", 
              backgroundColor: "#ffebee", 
              borderRadius: "4px", 
              borderLeft: "4px solid #f44336",
              textAlign: "center"
            }}>
              <div style={{ fontSize: "1.5em", fontWeight: "600", color: "#c62828" }}>
                {logs.filter(l => l.status === "failed").length}
              </div>
              <div style={{ color: "#555", marginTop: "5px", fontSize: "0.9em" }}>Failed</div>
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
              backgroundColor: "white",
              borderRadius: "4px",
              border: "1px solid #e0e0e0"
            }}>
              <thead>
                <tr style={{ backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }}>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", fontSize: "0.9em" }}>Status</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", fontSize: "0.9em" }}>Customer</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", fontSize: "0.9em" }}>Phone</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", fontSize: "0.9em" }}>Expiry Date</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", fontSize: "0.9em" }}>Sent At</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, index) => (
                  <tr 
                    key={log._id}
                    onClick={() => {
                      setSelectedLog(log);
                      if (log.messageId) {
                        fetchAnalytics(log.messageId);
                      }
                    }}
                    style={{
                      borderBottom: "1px solid #e0e0e0",
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "white",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                      textAlign: "left"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f0f0f0"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#fafafa" : "white"}
                  >
                    <td style={{ padding: "12px" }}>
                      <span style={{
                        display: "inline-block",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "0.85em",
                        fontWeight: "600",
                        backgroundColor: log.status === "success" ? "#c8e6c9" : "#ffcdd2",
                        color: log.status === "success" ? "#2e7d32" : "#c62828"
                      }}>
                        {log.status === "success" ? "✓ Delivered" : "✗ Failed"}
                      </span>
                    </td>
                    <td style={{ padding: "12px", fontSize: "0.95em" }}>{log.customerName}</td>
                    <td style={{ padding: "12px", fontSize: "0.95em" }}>{log.customerNumber}</td>
                    <td style={{ padding: "12px", fontSize: "0.95em" }}>{formatExpiryDate(log.expiryDate)}</td>
                    <td style={{ padding: "12px", fontSize: "0.9em", color: "#666" }}>
                      {formatDate(log.sentAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Detail Modal */}
          {selectedLog && (
            <div style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            textAlign: "left"
            }}>
              <div style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "30px",
                maxWidth: "600px",
                width: "90%",
                maxHeight: "80vh",
                overflowY: "auto",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <h2 style={{ margin: 0 }}>SMS Details</h2>
                  <button
                    onClick={() => {
                      setSelectedLog(null);
                      setAnalyticsData(null);
                      setAnalyticsError(null);
                      setBookingsData([]);
                      setBookingsError(null);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "1.5em",
                      cursor: "pointer",
                      color: "#666"
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Analytics Section - Moved to Top */}
                {selectedLog.messageId && (
                  <div style={{ marginBottom: "25px", paddingBottom: "20px", borderBottom: "2px solid #e0e0e0" }}>
                    <label style={{ display: "block", fontWeight: "600", marginBottom: "15px", color: "#00adb5", fontSize: "1.1em" }}>
                      📊 Link Analytics
                    </label>
                    
                    {analyticsLoading && (
                      <div style={{ display: "flex", justifyContent: "center", padding: "20px", transform: "scale(0.7)", transformOrigin: "center" }}>
                        <LoadingSpinner />
                      </div>
                    )}
                    
                    {analyticsError && (
                      <p style={{ color: "#c62828", backgroundColor: "#ffebee", padding: "10px", borderRadius: "4px" }}>
                        {analyticsError}
                      </p>
                    )}
                    
                    {analyticsData && analyticsData.length > 0 && analyticsData[0].links && (
                      <div>
                        {/* Link Stats */}
                        <div style={{ 
                          display: "grid", 
                          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", 
                          gap: "10px",
                          marginBottom: "20px"
                        }}>
                          <div style={{ 
                            backgroundColor: "#e3f2fd", 
                            padding: "12px", 
                            borderRadius: "4px",
                            textAlign: "center",
                            borderLeft: "3px solid #2196f3"
                          }}>
                            <div style={{ fontSize: "1.3em", fontWeight: "600", color: "#1976d2" }}>
                              {analyticsData[0].links.total}
                            </div>
                            <div style={{ fontSize: "0.8em", color: "#666", marginTop: "3px" }}>Total Sent</div>
                          </div>
                          <div style={{ 
                            backgroundColor: "#e8f5e9", 
                            padding: "12px", 
                            borderRadius: "4px",
                            textAlign: "center",
                            borderLeft: "3px solid #4caf50"
                          }}>
                            <div style={{ fontSize: "1.3em", fontWeight: "600", color: "#2e7d32" }}>
                              {analyticsData[0].links.opened_unique}
                            </div>
                            <div style={{ fontSize: "0.8em", color: "#666", marginTop: "3px" }}>Unique Clicks</div>
                          </div>
                          <div style={{ 
                            backgroundColor: "#fff3e0", 
                            padding: "12px", 
                            borderRadius: "4px",
                            textAlign: "center",
                            borderLeft: "3px solid #ff9800"
                          }}>
                            <div style={{ fontSize: "1.3em", fontWeight: "600", color: "#e65100" }}>
                              {analyticsData[0].links.opened_total}
                            </div>
                            <div style={{ fontSize: "0.8em", color: "#666", marginTop: "3px" }}>Total Clicks</div>
                          </div>
                          <div style={{ 
                            backgroundColor: "#f3e5f5", 
                            padding: "12px", 
                            borderRadius: "4px",
                            textAlign: "center",
                            borderLeft: "3px solid #9c27b0"
                          }}>
                            <div style={{ fontSize: "1.3em", fontWeight: "600", color: "#6a1b9a" }}>
                              {analyticsData[0].links.unopened}
                            </div>
                            <div style={{ fontSize: "0.8em", color: "#666", marginTop: "3px" }}>Unopened</div>
                          </div>
                        </div>

                      </div>
                    )}
                  </div>
                )}

                <table style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginBottom: "20px"
                }}>
                  <tbody>
                    <tr style={{ borderBottom: "1px solid #e0e0e0" }}>
                      <td style={{ padding: "10px", fontWeight: "600", color: "#00adb5", width: "35%" }}>Status:</td>
                      <td style={{ padding: "10px" }}>
                        <span style={{
                          display: "inline-block",
                          padding: "6px 12px",
                          borderRadius: "4px",
                          backgroundColor: selectedLog.status === "success" ? "#c8e6c9" : "#ffcdd2",
                          color: selectedLog.status === "success" ? "#2e7d32" : "#c62828",
                          fontWeight: "600"
                        }}>
                          {selectedLog.status === "success" ? "✓ Delivered" : "✗ Failed"}
                        </span>
                      </td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #e0e0e0", backgroundColor: "#f9f9f9" }}>
                      <td style={{ padding: "10px", fontWeight: "600", color: "#00adb5" }}>Customer Name:</td>
                      <td style={{ padding: "10px", color: "#555" }}>{selectedLog.customerName}</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #e0e0e0" }}>
                      <td style={{ padding: "10px", fontWeight: "600", color: "#00adb5" }}>Phone Number:</td>
                      <td style={{ padding: "10px", color: "#555" }}>{selectedLog.customerNumber}</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #e0e0e0", backgroundColor: "#f9f9f9" }}>
                      <td style={{ padding: "10px", fontWeight: "600", color: "#00adb5" }}>Certificate Expiry:</td>
                      <td style={{ padding: "10px", color: "#555" }}>{formatExpiryDate(selectedLog.expiryDate)}</td>
                    </tr>
                    
                    <tr >
                      <td style={{ padding: "10px", fontWeight: "600", color: "#00adb5" }}>Sent At:</td>
                      <td style={{ padding: "10px", color: "#555" }}>{formatDate(selectedLog.sentAt)}</td>
                    </tr>
                    {selectedLog.messageId && (
                      <tr style={{ borderBottom: "1px solid #e0e0e0", backgroundColor: "#f9f9f9" }}>
                        <td style={{ padding: "10px", fontWeight: "600", color: "#00adb5" }}>Message ID:</td>
                        <td style={{ padding: "10px" }}>
                          <code style={{ fontFamily: "monospace", fontSize: "0.85em", backgroundColor: "#f0f0f0", padding: "2px 6px", borderRadius: "3px", color: "#555" }}>
                            {selectedLog.messageId}
                          </code>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
<br/>
                <div style={{ marginBottom: "20px", paddingBottom: "20px" }}>
                  <h4 style={{ margin: "0 0 10px 0", color: "#00adb5", fontSize: "0.95em" }}>📝 Message:</h4>
                  <p style={{ margin: "0", color: "#555", lineHeight: "1.6", backgroundColor: "#f9f9f9", padding: "12px", borderRadius: "4px", fontSize: "0.9em" }}>
                    {selectedLog.messageSent}
                  </p>
                </div>

                {/* Bookings Section */}
                <div style={{ marginBottom: "20px", paddingTop: "30px", borderTop: "2px solid #e0e0e0" }}>
                  <label style={{ display: "block", fontWeight: "600", marginBottom: "15px", color: "#00adb5", fontSize: "1.1em" }}>
                    📅 Bookings After SMS
                  </label>
                  
                  {bookingsLoading && (
                    <div style={{ display: "flex", justifyContent: "center", padding: "20px", transform: "scale(0.7)", transformOrigin: "center" }}>
                      <LoadingSpinner />
                    </div>
                  )}
                  
                  {bookingsError && (
                    <p style={{ color: "#c62828", backgroundColor: "#ffebee", padding: "10px", borderRadius: "4px" }}>
                      {bookingsError}
                    </p>
                  )}
                  
                  {!bookingsLoading && !bookingsError && bookingsData.length === 0 && (
                    <p style={{ color: "#666", backgroundColor: "#f0f0f0", padding: "10px", borderRadius: "4px" }}>
                      No bookings made after this SMS was sent.
                    </p>
                  )}
            
                  {!bookingsLoading && bookingsData.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      <div style={{ color: "#666", fontSize: "0.9em", marginBottom: "10px" }}>
                        Found <strong>{bookingsData.length}</strong> booking{bookingsData.length !== 1 ? 's' : ''}
                      </div>
                      {bookingsData.map((booking: any, idx: number) => (
                        <div 
                          key={idx}
                          style={{
                            backgroundColor: "#f9f9f9",
                            padding: "10px",
                            borderRadius: "4px",
                            borderLeft: "3px solid #00adb5"
                          }}
                        >
                          <div style={{ fontWeight: "600", color: "#333", marginBottom: "5px" }}>
                            {booking.name || "Unknown"}
                          </div>
                          <div style={{ fontSize: "0.85em", color: "#666" }}>
                            📅 {new Date(booking.date).toLocaleDateString("en-GB")}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {selectedLog.error && (
                  <div style={{ marginBottom: "15px" }}>
                    <label style={{ display: "block", fontWeight: "600", marginBottom: "5px", color: "#c62828" }}>Error:</label>
                    <p style={{ margin: "0", color: "#c62828", backgroundColor: "#ffebee", padding: "10px", borderRadius: "4px" }}>
                      {selectedLog.error}
                    </p>
                  </div>
                )}

                <button
                  onClick={() => {
                    setSelectedLog(null);
                    setAnalyticsData(null);
                    setAnalyticsError(null);
                    setBookingsData([]);
                    setBookingsError(null);
                  }}
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: "#00adb5",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "600",
                    marginTop: "10px"
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </>
      )}
      <div style={{ height: "60px" }} />
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: "12px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: "0.85em",
  color: "#333",
};

const tdStyle: React.CSSProperties = {
  padding: "12px",
  fontSize: "0.85em",
  color: "#333",
  verticalAlign: "top",
  textAlign: "left",
};

const paginationBtnStyle = (disabled: boolean, active: boolean): React.CSSProperties => ({
  padding: active ? "4px 8px" : "6px 10px",
  backgroundColor: active ? "#00adb5" : disabled ? "#e5e5e5" : "#f0f0f0",
  color: active ? "#fff" : disabled ? "#999" : "#333",
  border: "1px solid #ddd",
  borderRadius: "4px",
  cursor: disabled ? "not-allowed" : "pointer",
  fontWeight: active ? 600 : 600,
  fontSize: "0.75em",
  minWidth: active ? "28px" : undefined,
  whiteSpace: "nowrap" as const,
});
